import React, { Component, CSSProperties } from 'react';

import classNames from 'classnames';
import { size as _size, isFunction } from 'lodash';
import t from 'common/utils/translate';
import ChangeIcon from 'common/components/Icon/Change';
import ImageUploadIcon from 'common/components/Icon/UploadImage';
import Text from 'admin/components/common/Text';
import { Icon } from 'admin/components/common/Icon';
import { Errors } from 'admin/components/common/Form/Errors';

import { ReduxFieldInputTypes, ReduxFieldMetaTypes } from '../utils';
import { ErrorTypes, validateImageDimensions, type FileDimensions } from './validation';

import css from './MediaField.scss';

const errMsgPath = 'form.mediaFieldErrors';

enum FileTypes {
	ALL_IMAGES_TYPES = 'ALL_IMAGES_TYPES',
	ONLY_PNG = 'ONLY_PNG',
	ONLY_JPG = 'ONLY_JPG',
	PNG_JPG = 'PNG_JPG',
	ICO_ONLY = 'ICO_ONLY',
	PNG_ICO = 'PNG_ICO',
}

const convertFileName = (val: string) => {
	const targetVal = val.lastIndexOf('/') !== -1 ? val.substring(val.lastIndexOf('/') + 1) : val;

	if (Object.values(ErrorTypes).includes(targetVal as ErrorTypes)) {
		return '';
	}

	if (_size(targetVal) > 25) {
		return `${targetVal.substring(0, 8)}...${targetVal.substring(targetVal.length - 10)}`;
	}

	return targetVal;
};

type Props = {
	input: ReduxFieldInputTypes;
	meta: ReduxFieldMetaTypes;
	showErrorsOn?: keyof ReduxFieldMetaTypes | ((meta: ReduxFieldMetaTypes, input: ReduxFieldInputTypes) => boolean);
	label: string;
	defaultValue?: string;
	description?: string;
	size?: 'small' | 'large';
	previewSize?: 'contain' | 'size';
	theme?: 'light' | 'dark';
	fileType?: FileTypes;
	fileSizeLimit?: number; // Mb
	fileDimensions?: FileDimensions;
	sizeInfoLabel?: string;
	clearable?: boolean;
	showInfo?: boolean;
	onBusyChange?: (busy: boolean) => void;
	eventListeners?: {
		onUpload: (data: { asset: File; name: string }) => Promise<Error | { asset: string }>;
	};
	className?: string;
};

type State = {
	preview: string;
	isBusy: boolean;
	fileName: string;
};

class MediaField extends Component<Props, State> {
	static fileTypes = FileTypes;

	static errors = ErrorTypes;

	static errorsMsg = {
		[MediaField.errors.UPLOAD_FAILED]: t(`${errMsgPath}.${MediaField.errors.UPLOAD_FAILED}`),
		[MediaField.errors.FILE_SIZE_LIMIT]: t(`${errMsgPath}.${MediaField.errors.FILE_SIZE_LIMIT}`),
		[MediaField.errors.FILE_WIDTH_HEIGHT_LARGE]: t(`${errMsgPath}.${MediaField.errors.FILE_WIDTH_HEIGHT_LARGE}`),
		[MediaField.errors.FILE_WIDTH_HEIGHT_SMALL]: t(`${errMsgPath}.${MediaField.errors.FILE_WIDTH_HEIGHT_SMALL}`),
	};

	static defaultProps = {
		previewSize: 'size',
		size: 'small',
		theme: 'dark',
		fileSizeLimit: 8,
		fileType: FileTypes.ALL_IMAGES_TYPES,
		showErrorsOn: 'touched',
		clearable: true,
		showInfo: true,
	};
	private mounted = false;
	private pendingUpload?: () => void;

	state = {
		isBusy: false,
		preview: this.props.input.value,
		fileName: convertFileName(this.props.input.value),
	};

	componentDidMount() {
		this.mounted = true;
	}

	componentDidUpdate(prevProps: Props) {
		const currentValue = this.props.input.value;

		if (typeof currentValue === 'string') {
			if (
				prevProps.input.value !== currentValue &&
				(Object.values(ErrorTypes) as string[]).includes(currentValue) === false
			) {
				this.setState({ preview: currentValue, fileName: convertFileName(currentValue) });
			}
		}
	}

	componentWillUnmount() {
		this.mounted = false;
		this.pendingUpload?.();
	}

	getAccept = (fileType: FileTypes) => {
		switch (fileType) {
			case MediaField.fileTypes.ALL_IMAGES_TYPES:
				return 'image/*';
			case MediaField.fileTypes.ONLY_PNG:
				return 'image/png';
			case MediaField.fileTypes.ONLY_JPG:
				return 'image/jpeg';
			case MediaField.fileTypes.PNG_JPG:
				return 'image/png, image/jpeg';
			case MediaField.fileTypes.ICO_ONLY:
				return 'image/x-icon';
			case MediaField.fileTypes.PNG_ICO:
				return 'image/png, image/x-icon';
			default:
				return 'image/*';
		}
	};

	getPreviewStyles = () => {
		const { preview } = this.state;
		const { previewSize } = this.props;
		const result: Pick<CSSProperties, 'backgroundImage' | 'backgroundSize'> = {};

		if (preview) {
			result.backgroundImage = `url('${preview}')`;
		}

		if (previewSize === 'size') {
			result.backgroundSize = '32px 32px';
		}

		if (previewSize === 'contain') {
			result.backgroundSize = 'contain';
		}

		return result;
	};

	checkFileDimensions = (fileSrc: string, fileDimensions?: FileDimensions): Promise<string> => {
		const img = new Image();

		return new Promise(resolve => {
			img.onload = () => resolve(validateImageDimensions(img.width, img.height, fileDimensions));
			img.onerror = () => resolve(MediaField.errors.UPLOAD_FAILED);
			img.src = fileSrc;
		});
	};

	onCancelImageClick = () => {
		if (this.pendingUpload) return;
		const { input, defaultValue } = this.props;

		if (defaultValue) {
			this.setState({ preview: defaultValue, fileName: convertFileName(defaultValue) });
			input.onChange(defaultValue);
		} else {
			this.setState({ preview: '', fileName: '' });
			input.onChange('');
		}
	};

	onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const target = e.currentTarget;
		const file = target.files?.[0];
		if (!file || this.pendingUpload) return;
		const { input, fileSizeLimit, fileDimensions, eventListeners, onBusyChange } = this.props;
		// Allow retrying the same file after a failed upload or validation.
		target.value = '';
		const setError = (error: string) => {
			input.onBlur(error);
			input.onChange(error);
		};
		if (fileSizeLimit && file.size > fileSizeLimit * 1024 * 1024) {
			setError(MediaField.errors.FILE_SIZE_LIMIT);
			return;
		}
		const finish = () => {
			if (this.pendingUpload !== finish) return;
			this.pendingUpload = undefined;
			if (this.mounted) this.setState({ isBusy: false });
			onBusyChange?.(false);
		};
		const isCurrentUpload = () => this.mounted && this.pendingUpload === finish;
		this.pendingUpload = finish;
		this.setState({ isBusy: true, fileName: convertFileName(file.name) });
		try {
			onBusyChange?.(true);
			const preview = await new Promise<string>((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () =>
					typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('File read failed.'));
				const onReadError = () => reject(reader.error ?? new Error('File read failed.'));
				reader.onerror = onReadError;
				reader.onabort = onReadError;
				reader.readAsDataURL(file);
			});
			if (!isCurrentUpload()) return;
			this.setState({ preview });
			const error = await this.checkFileDimensions(preview, fileDimensions);
			if (!isCurrentUpload()) return;
			if (error) {
				setError(error);
				return;
			}
			const result = await eventListeners?.onUpload?.({ name: input.name, asset: file });
			// Use this attempt's callbacks; an unmounted field must never apply its late result.
			if (!isCurrentUpload()) return;
			if (result && 'asset' in result) input.onChange(result.asset);
			else setError(MediaField.errors.UPLOAD_FAILED);
		} catch {
			if (isCurrentUpload()) setError(MediaField.errors.UPLOAD_FAILED);
		} finally {
			finish();
		}
	};

	render() {
		const {
			meta,
			input,
			size = 'small',
			theme = 'dark',
			label,
			description,
			fileType,
			fileDimensions,
			showErrorsOn,
			clearable,
			showInfo,
			className = '',
		} = this.props;
		const { preview, isBusy, fileName } = this.state;
		const id = `${meta.form}.${input.name}`;
		const sizeInfo =
			this.props.sizeInfoLabel || (fileDimensions ? `${fileDimensions.maxW}x${fileDimensions.maxH}px` : '');

		// file upload could be delayed (organization general settings page) and in meanwhile value is data-url
		// only after successful upload value will be updated with real url, so we need to check if value is url:
		const hasLinkToOriginal = fileName && typeof input.value === 'string' && /^https?:\/\//i.test(input.value);
		const fileNameTextProps = {
			tag: hasLinkToOriginal ? ('a' as const) : ('div' as const),
			size: Text.size.label,
			text: hasLinkToOriginal ? 'Download original' : description,
			href: hasLinkToOriginal && typeof input.value === 'string' ? input.value.split('?').shift() : undefined,
			target: hasLinkToOriginal ? '_blank' : undefined,
			rel: hasLinkToOriginal ? 'noreferrer noopener' : undefined,
		};

		const showErrors = isFunction(showErrorsOn) ? showErrorsOn(meta, input) : meta[showErrorsOn || ''];
		const isCancelDisabled =
			this.props.input.value.replace(/\?.*/, '') === this.props.defaultValue?.replace(/\?.*/, '');

		return (
			<div
				className={classNames(
					css.mediaField,
					meta.error && css.error,
					css[size],
					css[theme],
					isBusy && css.busy,
					preview && css.hasPreview,
					className
				)}
			>
				<div className={css.inputWrap}>
					<label htmlFor={id}>
						{preview ? (
							<div className={css.preview} style={this.getPreviewStyles()}>
								<div className={css.changeIconBtn}>
									<ChangeIcon />
								</div>
							</div>
						) : (
							<div className={css.uploadImageIcon}>
								<ImageUploadIcon
									width={size === 'large' ? '68px' : '28px'}
									height={size === 'large' ? '68px' : '28px'}
									color={
										theme === 'dark' ? 'var(--ra-color-white-dark)' : 'var(--ra-color-black-mid)'
									}
								/>
							</div>
						)}
						<svg className={css.preloader} viewBox="25 25 50 50">
							<circle
								cx="50"
								cy="50"
								r="20"
								fill="none"
								strokeWidth="2"
								strokeMiterlimit="10"
								strokeDasharray="100 25"
							/>
						</svg>
						<input
							type="file"
							aria-label={label}
							accept={this.getAccept(fileType!)}
							id={id}
							name={input.name}
							onChange={this.onFileChange}
							disabled={isBusy || meta.submitting}
						/>
						{size === 'large' && (
							<div className={css.info}>
								<Text {...fileNameTextProps} />
								<Text size="label" tag="div" text={sizeInfo} />
								<Errors className={css.error} show={showErrors}>
									{meta.error}
								</Errors>
							</div>
						)}
						{size === 'large' && clearable && (
							<button
								type="button"
								onClick={this.onCancelImageClick}
								className={css.cancelIconBtn}
								disabled={isCancelDisabled || isBusy || meta.submitting}
							>
								<Icon type="x-rounded" width={24} color="var(--ra-color-white-dark)" />
							</button>
						)}
					</label>
				</div>
				{size === 'small' ? (
					showInfo ? (
						<div className={css.info}>
							<Text className={css.label} weight="bold" size="label" tag="div" text={label} />
							<Text {...fileNameTextProps} />
							<Text size="label" tag="div" text={sizeInfo} />
							<Errors className={css.error} show={showErrors}>
								{meta.error}
							</Errors>

							{clearable && (
								<button
									type="button"
									onClick={this.onCancelImageClick}
									className={css.cancelIconBtn}
									disabled={isCancelDisabled || isBusy || meta.submitting}
								>
									<Icon type="x-rounded" width={24} color="var(--ra-color-white-dark)" />
								</button>
							)}
						</div>
					) : null
				) : (
					<Text className={css.label} weight="bold" size="label" tag="div" text={label} />
				)}
			</div>
		);
	}
}

export default MediaField;
